/**
 * Tela pública do operador — layout de documento oficial.
 *
 * URL: /inovacao/analise/:token
 * Exige JWT (usuário logado no CRM). Token identifica o recurso, não autentica.
 *
 * Estrutura do documento (bate com o PDF de referência do Victor):
 *   Header Elosaúde + título + status pendente/concluído
 *   1. Dados do Projeto
 *   2. Resumo Executivo
 *   3. Análise Comparativa (Manual vs Automático) — read-only
 *   4. Análise de Esforço em Tarefas Manuais (questionário de 8 perguntas)
 *   Botões: Salvar rascunho, Submeter, Imprimir/Baixar PDF (window.print)
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Printer, Send, Save } from 'lucide-react';
import { analisePublicaAPI } from '../../services/inovacaoAPI';
import { useBranding, logoHeader } from '../../context/BrandingContext';

const BRAND = 'var(--brand, #20a490)';
const ROTULO_ESTADO = {
  rascunho: { label: 'Rascunho', cor: '#95a5a6' },
  aguardando_respostas: { label: 'Pendente com operador', cor: '#f39c12' },
  em_revisao_gni: { label: 'Respondido, em revisão pelo GNI', cor: '#3498db' },
  em_aprovacao: { label: 'Em aprovação', cor: '#9b59b6' },
  aprovada: { label: 'Aprovada', cor: '#27ae60' },
  rejeitada: { label: 'Rejeitada', cor: '#e74c3c' },
  backlog: { label: 'Backlog', cor: '#7f8c8d' },
  projeto_criado: { label: 'Virou projeto', cor: 'var(--brand, #20a490)' },
  cancelada: { label: 'Cancelada', cor: '#bdc3c7' },
};

export default function AnalisePublica() {
  const { token } = useParams();
  const { branding } = useBranding();

  const [analise, setAnalise] = useState(null);
  const [form, setForm] = useState({
    frequencia_verificacao: '',
    tempo_por_verificacao_minutos: '',
    impacto_outras_tarefas: 3,
    carga_cognitiva: 3,
    estresse: '',
    riscos_tarefa_manual: '',
    impacto_rotina: '',
    outras_dificuldades: '',
    envolve_outros_setores: '',
    observacao_adicional: '',
  });
  const [saving, setSaving] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    analisePublicaAPI.obter(token)
      .then((d) => {
        setAnalise(d);
        if (d.resposta) {
          setForm({
            frequencia_verificacao: d.resposta.frequencia_verificacao || '',
            tempo_por_verificacao_minutos: d.resposta.tempo_por_verificacao_minutos || '',
            impacto_outras_tarefas: d.resposta.impacto_outras_tarefas || 3,
            carga_cognitiva: d.resposta.carga_cognitiva || 3,
            estresse: d.resposta.estresse || '',
            riscos_tarefa_manual: d.resposta.riscos_tarefa_manual || '',
            impacto_rotina: d.resposta.impacto_rotina || '',
            outras_dificuldades: d.resposta.outras_dificuldades || '',
            envolve_outros_setores: d.resposta.envolve_outros_setores || '',
            observacao_adicional: d.resposta.observacao_adicional || '',
          });
        }
      })
      .catch((e) => toast.error(e.response?.data?.detail?.message || 'Token inválido'));
  }, [token]);

  useEffect(() => {
    if (!analise || analise.estado !== 'aguardando_respostas') return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      analisePublicaAPI.salvarRascunho(token, form).catch(() => {});
    }, 5000);
    return () => clearTimeout(timerRef.current);
  }, [form, analise, token]);

  async function salvarManual() {
    setSaving(true);
    try {
      await analisePublicaAPI.salvarRascunho(token, form);
      toast.success('Rascunho salvo');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  }

  async function submeter() {
    const obrig = ['frequencia_verificacao', 'tempo_por_verificacao_minutos',
                   'riscos_tarefa_manual', 'impacto_rotina'];
    for (const c of obrig) {
      if (!form[c] || (typeof form[c] === 'string' && !form[c].trim())) {
        toast.error('Preencha todos os campos obrigatórios marcados com *'); return;
      }
    }
    setSaving(true);
    try {
      const atualizada = await analisePublicaAPI.submeter(token, form);
      toast.success(`Enviado! Score calculado: ${atualizada.score_oportunidade}`);
      const d = await analisePublicaAPI.obter(token);
      setAnalise(d);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail?.code || 'Erro ao enviar');
    } finally { setSaving(false); }
  }

  if (!analise) return <div style={{ padding: 40 }}>Carregando…</div>;

  const readOnly = analise.estado !== 'aguardando_respostas';
  const estadoInfo = ROTULO_ESTADO[analise.estado] || { label: analise.estado, cor: '#95a5a6' };

  return (
    <div style={estilos.pagina}>
      {/* CSS @print injetado diretamente */}
      <style>{CSS_PRINT}</style>

      <div style={estilos.folha} className="doc-folha">
        {/* Cabeçalho com logo */}
        <header style={estilos.cabecalho} className="doc-cabecalho">
          <img src={logoHeader(branding)} alt={branding.nome_operadora} style={estilos.logo} />
          <div style={{ flex: 1 }}>
            <h1 style={estilos.tituloPrincipal}>{analise.titulo}</h1>
            <p style={estilos.subtitulo}>Oportunidade e Esforço · Governança de Novas Iniciativas</p>
          </div>
          <div style={{ ...estilos.badgeEstado, background: estadoInfo.cor }} className="no-print">
            {estadoInfo.label}
          </div>
        </header>

        {/* Botões de ação */}
        <div style={estilos.barraAcoes} className="no-print">
          <button onClick={() => window.print()} style={estilos.btnSecundario}>
            <Printer size={14} /> Imprimir / Baixar PDF
          </button>
          {!readOnly && (
            <>
              <button onClick={salvarManual} disabled={saving} style={estilos.btnSecundario}>
                <Save size={14} /> Salvar rascunho
              </button>
              <button onClick={submeter} disabled={saving} style={estilos.btnPrimario}>
                <Send size={14} /> Submeter respostas
              </button>
            </>
          )}
        </div>

        {/* 1. Dados do Projeto */}
        <Secao numero="" titulo="Dados do Projeto">
          <dl style={estilos.dl}>
            <KV label="Nome do Projeto" value={analise.titulo} />
            <KV label="Responsável Técnico"
                value={analise.responsavel_tecnico?.nome || '—'} />
            <KV label="Gerente da Área"
                value={analise.gerente_area?.nome || '—'} />
            <KV label="Propriedade" value={analise.propriedade} />
            <KV label="Setor Mais Impactado"
                value={analise.setor_solicitante?.nome} />
            {analise.setores_impactados?.length > 0 && (
              <KV label="Outros Setores Impactados"
                  value={analise.setores_impactados.map(s => s.nome).join(', ')} />
            )}
          </dl>
        </Secao>

        {/* 2. Resumo Executivo */}
        <Secao numero="" titulo="Resumo Executivo">
          <p style={estilos.paragrafo}>
            {analise.resumo_executivo
              ? analise.resumo_executivo
              : <em style={{ color: '#aaa' }}>Resumo não preenchido pelo analista.</em>}
          </p>
          {analise.objetivo && (
            <>
              <h3 style={estilos.subSecao}>Objetivo</h3>
              <p style={estilos.paragrafo}>{analise.objetivo}</p>
            </>
          )}
        </Secao>

        {/* 3. Análise Comparativa */}
        {analise.comparativa?.length > 0 && (
          <Secao numero="" titulo="Análise Comparativa do Processo">
            <p style={{ ...estilos.paragrafo, fontStyle: 'italic', color: '#555' }}>
              Comparação lado a lado do processo manual vs. processo automático proposto.
            </p>
            <table style={estilos.tabelaComp}>
              <thead>
                <tr>
                  <th style={estilos.thComp}>Característica</th>
                  <th style={estilos.thComp}>Processo Manual (atual)</th>
                  <th style={estilos.thComp}>Processo Automático (proposto)</th>
                </tr>
              </thead>
              <tbody>
                {analise.comparativa
                  .slice()
                  .sort((a, b) => a.ordem - b.ordem)
                  .map((c, i) => (
                    <tr key={i}>
                      <td style={{ ...estilos.tdComp, fontWeight: 600 }}>{c.caracteristica}</td>
                      <td style={estilos.tdComp}>{c.processo_manual || <em style={{ color: '#aaa' }}>—</em>}</td>
                      <td style={estilos.tdComp}>{c.processo_automatico || <em style={{ color: '#aaa' }}>—</em>}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Secao>
        )}

        {/* 4. Análise de Esforço em Tarefas Manuais — Questionário */}
        <Secao numero="" titulo="Análise de Esforço em Tarefas Manuais">
          <p style={{ ...estilos.paragrafo, color: '#555', marginBottom: 24 }}>
            Para quantificar a oportunidade, solicitamos que o operador responda ao questionário
            a seguir. Essas respostas alimentam o cálculo do score de oportunidade.
          </p>

          <Pergunta n="1" titulo="Frequência" obrigatorio readOnly={readOnly}>
            Quantas vezes esse processo manual é realizado?
            <input style={estilos.input} value={form.frequencia_verificacao}
                   readOnly={readOnly}
                   placeholder="Ex: 4 vezes por mês, 8 horas por dia..."
                   onChange={(e) => setForm({ ...form, frequencia_verificacao: e.target.value })} />
          </Pergunta>

          <Pergunta n="2" titulo="Esforço" obrigatorio readOnly={readOnly}>
            Em média, quantos <b>minutos</b> por execução (busca, agrupamento, conferência)?
            <input type="number" step="0.1" min="0.1" style={estilos.input}
                   value={form.tempo_por_verificacao_minutos} readOnly={readOnly}
                   placeholder="Ex: 180 (= 3h)"
                   onChange={(e) => setForm({ ...form, tempo_por_verificacao_minutos: e.target.value })} />
          </Pergunta>

          <Pergunta n="3" titulo="Impacto em Outras Tarefas" obrigatorio readOnly={readOnly}>
            Em uma escala de 1 a 5 (1 = nenhum impacto, 5 = impacto total),
            como essa atividade manual afeta outras tarefas da equipe?
            <EscalaPontos valor={form.impacto_outras_tarefas} readOnly={readOnly}
                          onChange={(v) => setForm({ ...form, impacto_outras_tarefas: v })} />
          </Pergunta>

          <Pergunta n="4" titulo="Nível de Concentração" obrigatorio readOnly={readOnly}>
            Escala de 1 a 5 (1 = baixo, 5 = extremamente alto): qual o nível de
            concentração necessário para evitar erros?
            <EscalaPontos valor={form.carga_cognitiva} readOnly={readOnly}
                          onChange={(v) => setForm({ ...form, carga_cognitiva: v })} />
          </Pergunta>

          <Pergunta n="5" titulo="Estresse" readOnly={readOnly}>
            Essa atividade causa estresse, ansiedade ou fadiga?
            <textarea style={{ ...estilos.input, minHeight: 60 }} value={form.estresse}
                      readOnly={readOnly}
                      placeholder="Ex: Além do estresse, causa fadiga na equipe."
                      onChange={(e) => setForm({ ...form, estresse: e.target.value })} />
          </Pergunta>

          <Pergunta n="6" titulo="Ocorrência de Erros" obrigatorio readOnly={readOnly}>
            Já ocorreram erros manuais? Quais as consequências?
            <textarea style={{ ...estilos.input, minHeight: 80 }} value={form.riscos_tarefa_manual}
                      readOnly={readOnly}
                      placeholder="Ex: sim, falta de pagamento expõe a multas/glosas/insatisfação..."
                      onChange={(e) => setForm({ ...form, riscos_tarefa_manual: e.target.value })} />
          </Pergunta>

          <Pergunta n="6a" titulo="Impacto na Rotina" obrigatorio readOnly={readOnly}>
            Como essa tarefa impacta a rotina da equipe?
            <textarea style={{ ...estilos.input, minHeight: 60 }} value={form.impacto_rotina}
                      readOnly={readOnly}
                      placeholder="Descreva como a tarefa consome tempo e afeta o dia a dia."
                      onChange={(e) => setForm({ ...form, impacto_rotina: e.target.value })} />
          </Pergunta>

          <Pergunta n="7" titulo="Outras Dificuldades" readOnly={readOnly}>
            Existe alguma outra dificuldade ou ponto de dor não mencionado?
            <textarea style={{ ...estilos.input, minHeight: 60 }} value={form.outras_dificuldades}
                      readOnly={readOnly}
                      placeholder="Ex: além dos pagamentos, fluxo de caixa também é manual."
                      onChange={(e) => setForm({ ...form, outras_dificuldades: e.target.value })} />
          </Pergunta>

          <Pergunta n="8" titulo="Envolve Outros Setores?" readOnly={readOnly}>
            Esta atividade depende ou envolve outros setores? Quais?
            <input style={estilos.input} value={form.envolve_outros_setores}
                   readOnly={readOnly}
                   placeholder="Ex: cadastro, reembolso, assessoria jurídica"
                   onChange={(e) => setForm({ ...form, envolve_outros_setores: e.target.value })} />
          </Pergunta>
        </Secao>

        {!readOnly && (
          <div style={estilos.barraAcoesRodape} className="no-print">
            <span style={{ color: '#7f8c8d', fontSize: '0.85rem' }}>
              Rascunho salvo automaticamente a cada 5 segundos.
            </span>
            <button onClick={submeter} disabled={saving} style={estilos.btnPrimario}>
              <Send size={16} /> Submeter respostas
            </button>
          </div>
        )}

        {readOnly && (
          <div style={estilos.avisoConcluido}>
            <b>Questionário concluído:</b> as respostas foram enviadas ao GNI para revisão.
          </div>
        )}

        <footer style={estilos.rodape}>
          Governança de Novas Iniciativas · {branding.nome_operadora} · {new Date().getFullYear()}
        </footer>
      </div>
    </div>
  );
}

// ============ Componentes internos ============

function Secao({ numero, titulo, children }) {
  return (
    <section style={estilos.secao}>
      <h2 style={estilos.tituloSecao}>
        {numero && <span style={{ color: BRAND, marginRight: 8 }}>{numero}</span>}
        {titulo}
      </h2>
      {children}
    </section>
  );
}

function KV({ label, value }) {
  return (
    <>
      <dt style={estilos.dt}>{label}:</dt>
      <dd style={estilos.dd}>{value}</dd>
    </>
  );
}

function Pergunta({ n, titulo, obrigatorio, children, readOnly }) {
  return (
    <div style={estilos.pergunta}>
      <div style={estilos.perguntaHeader}>
        <span style={{ ...estilos.perguntaNumero, background: BRAND }}>{n}</span>
        <h4 style={estilos.perguntaTitulo}>
          {titulo} {obrigatorio && !readOnly && <span style={{ color: '#e74c3c' }}>*</span>}
        </h4>
      </div>
      <div style={{ marginLeft: 48, color: '#555' }}>{children}</div>
    </div>
  );
}

function EscalaPontos({ valor, readOnly, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => onChange(n)}
          style={{
            width: 44, height: 44, borderRadius: 22,
            border: `2px solid ${valor === n ? BRAND : '#ccc'}`,
            background: valor === n ? BRAND : '#fff',
            color: valor === n ? '#fff' : '#555',
            fontWeight: 700, fontSize: '1rem',
            cursor: readOnly ? 'default' : 'pointer',
            transition: 'all 0.15s',
          }}>
          {n}
        </button>
      ))}
      <span style={{ marginLeft: 8, color: '#777', alignSelf: 'center', fontSize: '0.85rem' }}>
        {valor === 1 && 'Mínimo'}
        {valor === 2 && 'Baixo'}
        {valor === 3 && 'Moderado'}
        {valor === 4 && 'Alto'}
        {valor === 5 && 'Extremo'}
      </span>
    </div>
  );
}

// ============ Estilos ============

const estilos = {
  pagina: { background: '#eef2f5', minHeight: '100vh', padding: '32px 16px' },
  folha: {
    maxWidth: 880, margin: '0 auto', background: '#fff',
    padding: '48px 56px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    borderRadius: 8,
  },
  cabecalho: {
    display: 'flex', alignItems: 'center', gap: 20,
    paddingBottom: 20, borderBottom: `3px solid ${BRAND}`, marginBottom: 32,
  },
  logo: { height: 64, width: 'auto' },
  tituloPrincipal: { margin: 0, color: '#2c3e50', fontSize: '1.8rem', fontWeight: 700 },
  subtitulo: { margin: '4px 0 0', color: '#7f8c8d', fontSize: '0.9rem' },
  badgeEstado: {
    color: '#fff', padding: '6px 14px', borderRadius: 16,
    fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap',
  },
  barraAcoes: {
    display: 'flex', gap: 8, marginBottom: 24, padding: 12,
    background: '#f8f9fa', borderRadius: 6, justifyContent: 'flex-end',
  },
  btnPrimario: {
    background: BRAND, color: '#fff', padding: '10px 20px',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.9rem',
    display: 'inline-flex', gap: 6, alignItems: 'center', fontWeight: 500,
  },
  btnSecundario: {
    background: '#fff', color: '#555', padding: '10px 16px',
    border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem',
    display: 'inline-flex', gap: 6, alignItems: 'center',
  },
  secao: { marginBottom: 40 },
  tituloSecao: {
    color: '#2c3e50', fontSize: '1.25rem',
    borderLeft: `4px solid ${BRAND}`, paddingLeft: 12,
    marginBottom: 16, marginTop: 0,
  },
  subSecao: { color: '#2c3e50', fontSize: '1rem', marginTop: 20, marginBottom: 8 },
  paragrafo: { color: '#333', lineHeight: 1.7, margin: 0, whiteSpace: 'pre-wrap' },
  dl: {
    display: 'grid', gridTemplateColumns: 'max-content 1fr',
    gap: '8px 20px', margin: 0,
  },
  dt: { fontWeight: 600, color: '#555' },
  dd: { margin: 0, color: '#2c3e50' },
  tabelaComp: {
    width: '100%', borderCollapse: 'collapse', marginTop: 12,
    fontSize: '0.9rem',
  },
  thComp: {
    background: '#f8f9fa', padding: '10px 14px', textAlign: 'left',
    borderBottom: `2px solid ${BRAND}`, fontWeight: 600, color: '#2c3e50',
  },
  tdComp: {
    padding: '10px 14px', borderBottom: '1px solid #ecf0f1',
    verticalAlign: 'top', color: '#444',
  },
  pergunta: {
    marginBottom: 24, padding: 16, background: '#f8f9fa',
    borderRadius: 6, borderLeft: `3px solid ${BRAND}`,
  },
  perguntaHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  perguntaNumero: {
    color: '#fff', width: 36, height: 36, borderRadius: 18,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: '0.85rem', flexShrink: 0,
  },
  perguntaTitulo: { margin: 0, color: '#2c3e50', fontSize: '1rem', fontWeight: 600 },
  input: {
    width: '100%', padding: 10, marginTop: 8,
    borderRadius: 4, border: '1px solid #ccc', fontSize: '0.9rem',
    fontFamily: 'inherit', background: '#fff',
  },
  barraAcoesRodape: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 0', borderTop: '1px solid #ecf0f1', marginTop: 24,
  },
  avisoConcluido: {
    background: '#d4efdf', padding: 16, borderRadius: 6, marginTop: 24,
    color: '#1e8449', textAlign: 'center',
  },
  rodape: {
    marginTop: 48, paddingTop: 16, borderTop: '1px solid #ecf0f1',
    color: '#95a5a6', fontSize: '0.75rem', textAlign: 'center',
  },
};

const CSS_PRINT = `
@media print {
  body { background: white !important; }
  .no-print { display: none !important; }
  .doc-folha { max-width: 100% !important; box-shadow: none !important; padding: 20px 0 !important; }
  .doc-cabecalho { page-break-after: avoid; }
  section { page-break-inside: avoid; }
  input, textarea { border: none !important; background: #fafafa !important; }
}
`;
